import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { kpisAPI, tasksAPI, habitsAPI, agendaAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  CheckCircle2,
  Calendar,
  Clock,
  Target,
  TrendingUp,
  BookOpen,
  Loader2,
  ChevronRight,
  Plus,
  Flame,
  Trash2,
  ListChecks,
  CalendarClock,
  AlertTriangle,
  Video,
  Megaphone,
  Users,
  Star,
  ExternalLink,
} from 'lucide-react';
import { Textarea } from './ui/textarea';
import { BirthdayReminder } from './BirthdayReminder';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function MemberDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [habitDialog, setHabitDialog] = useState(false);
  const [habitName, setHabitName] = useState('');
  const [habitSaving, setHabitSaving] = useState(false);
  const [agenda, setAgenda] = useState([]);
  const [eventDialog, setEventDialog] = useState(false);
  const [eventForm, setEventForm] = useState({ titulo: '', hora: '', nota: '' });
  const [eventSaving, setEventSaving] = useState(false);

  useEffect(() => {
    loadData();
    // Sincronización en tiempo real
    const id = setInterval(() => loadData(true), 15000);
    return () => clearInterval(id);
  }, []);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [kpisData, tasksData, habitsData, agendaData] = await Promise.all([
        kpisAPI.getMember(),
        tasksAPI.getAll(),
        habitsAPI.getAll(),
        agendaAPI.getToday(),
      ]);
      setKpis(kpisData);
      setTasks((tasksData || []).filter(t => t.status !== 'completed'));
      setHabits((habitsData || []).slice(0, 6));
      setAgenda(agendaData?.items || []);
    } catch (error) {
      console.error('Error loading member dashboard:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.titulo.trim()) return;
    setEventSaving(true);
    try {
      const todayIso = new Date().toISOString().split('T')[0];
      await agendaAPI.createPersonalEvent({
        titulo: eventForm.titulo.trim(),
        fecha: todayIso,
        hora: eventForm.hora || null,
        nota: eventForm.nota || null,
      });
      setEventForm({ titulo: '', hora: '', nota: '' });
      setEventDialog(false);
      loadData(true);
    } catch (error) {
      console.error('Error creating personal event:', error);
    }
    setEventSaving(false);
  };

  const handleDeleteEvent = async (id) => {
    try {
      await agendaAPI.deletePersonalEvent(id);
      loadData(true);
    } catch (error) {
      console.error('Error deleting personal event:', error);
    }
  };

  const handleCreateHabit = async () => {
    if (!habitName.trim()) return;
    setHabitSaving(true);
    try {
      await habitsAPI.create({ name: habitName.trim() });
      setHabitName('');
      setHabitDialog(false);
      loadData();
    } catch (error) {
      console.error('Error creating habit:', error);
    }
    setHabitSaving(false);
  };

  // Derived task summary (real-time, same source as "Mis Tareas")
  const todayStr = new Date().toISOString().split('T')[0];
  const todayCount = tasks.filter(t => (t.due_date || '').slice(0, 10) === todayStr).length;
  const highCount = tasks.filter(t => t.priority === 'high').length;
  const tasksByClient = React.useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => { const k = t.client_group_id || '__none__'; const g = m.get(k) || { key: k, name: t.client_group_name || 'Sin cliente', count: 0 }; g.count += 1; m.set(k, g); });
    return Array.from(m.values()).sort((a, b) => (a.key === '__none__') - (b.key === '__none__') || b.count - a.count);
  }, [tasks]);

  const handleHabitLog = async (habitId, completed) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await habitsAPI.log(habitId, today, completed);
      loadData();
    } catch (error) {
      console.error('Error logging habit:', error);
    }
  };

  const handleDeleteHabit = async (habitId) => {
    if (!window.confirm('¿Eliminar este hábito?')) return;
    try {
      await habitsAPI.delete(habitId);
      loadData();
    } catch (error) {
      console.error('Error deleting habit:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="member-dashboard"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {greeting()}, {user?.name?.split(' ')[0] || 'Usuario'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Aquí está el resumen de tu día
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </motion.div>

      {/* Recordatorio de cumpleaños del equipo */}
      <motion.div variants={itemVariants}>
        <BirthdayReminder />
      </motion.div>

      {/* Quick Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover kpi-emerald">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Tareas Pendientes</p>
                <p className="text-2xl font-bold">{kpis?.pending_tasks || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover kpi-blue">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Eventos Hoy</p>
                <p className="text-2xl font-bold">{agenda.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover kpi-amber">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Racha Journal</p>
                <p className="text-2xl font-bold">{kpis?.journal_streak || 0} {(kpis?.journal_streak === 1) ? 'día' : 'días'}</p>
              </div>
              <BookOpen className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Main Content - 3 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Tasks & Agenda */}
        <motion.div variants={itemVariants} className="space-y-6">
          {/* Resumen de tareas (vistazo rápido; el detalle está en "Mis Tareas") */}
          <Card
            className="card-hover cursor-pointer"
            onClick={() => navigate('/tasks')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/tasks'); }}
            data-testid="tasks-summary-card"
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  Mis Tareas
                </CardTitle>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-muted/40 p-3 text-center" data-testid="summary-pending">
                  <ListChecks className="w-5 h-5 mx-auto text-primary mb-1" />
                  <p className="text-2xl font-bold leading-none">{tasks.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">Pendientes</p>
                </div>
                <div className="rounded-lg bg-blue-50 p-3 text-center" data-testid="summary-today">
                  <CalendarClock className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold leading-none text-blue-700">{todayCount}</p>
                  <p className="text-xs text-muted-foreground mt-1">Para hoy</p>
                </div>
                <div className="rounded-lg bg-red-50 p-3 text-center" data-testid="summary-high">
                  <AlertTriangle className="w-5 h-5 mx-auto text-red-600 mb-1" />
                  <p className="text-2xl font-bold leading-none text-red-700">{highCount}</p>
                  <p className="text-xs text-muted-foreground mt-1">Alta prioridad</p>
                </div>
              </div>
              {tasksByClient.length > 0 && (
                <div className="mt-4 space-y-1" data-testid="dashboard-tasks-by-client">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Por cliente</p>
                  {tasksByClient.map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); navigate(g.key === '__none__' ? '/tasks' : `/tasks?client=${g.key}`); }}
                      className="w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-muted/60 transition-colors"
                      data-testid={`dashboard-client-${g.key}`}
                    >
                      <span className="truncate">{g.name}</span>
                      <span className="ml-2 inline-flex min-w-[20px] justify-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-800">{g.count}</span>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-sm text-primary font-medium mt-4 flex items-center gap-1">
                Ver todas en Mis Tareas <ChevronRight className="w-4 h-4" />
              </p>
            </CardContent>
          </Card>

          {/* Agenda de Hoy - agregador interno del portal */}
          <Card className="card-hover">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Agenda de Hoy
                </CardTitle>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/events')} data-testid="open-calendar-btn" title="Abrir mi calendario">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="today-agenda">
              {agenda.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tienes nada en tu agenda hoy
                </p>
              ) : (
                agenda.map((item, index) => {
                  const cfg = {
                    reunion: { icon: Users, color: 'text-blue-600', bar: 'bg-blue-500', label: 'Reunión' },
                    meeting: { icon: Users, color: 'text-blue-600', bar: 'bg-blue-500', label: 'Reunión' },
                    grabacion: { icon: Video, color: 'text-purple-600', bar: 'bg-purple-500', label: 'Grabación' },
                    tarea: { icon: CheckCircle2, color: 'text-emerald-600', bar: 'bg-emerald-500', label: 'Tarea' },
                    task: { icon: CheckCircle2, color: 'text-emerald-600', bar: 'bg-emerald-500', label: 'Tarea' },
                    publication: { icon: Megaphone, color: 'text-coral-600', bar: 'bg-[#f56258]', label: 'Publicación' },
                    personal: { icon: Star, color: 'text-amber-600', bar: 'bg-amber-500', label: 'Personal' },
                  }[item.type] || { icon: Calendar, color: 'text-primary', bar: 'bg-primary', label: '' };
                  const Icon = cfg.icon;
                  const isTask = item.type === 'task' || item.type === 'tarea';
                  const clickable = isTask || item.type === 'publication' || item.source_kind === 'client_meeting';
                  return (
                    <div
                      key={`${item.type}-${item.id || index}`}
                      className={`group flex items-center gap-3 p-3 rounded-lg bg-muted/30 ${clickable ? 'hover:bg-muted/60 cursor-pointer transition-colors' : ''}`}
                      onClick={() => {
                        if (isTask) navigate(`/tasks?task=${item.id}`);
                        else if (item.type === 'publication') navigate('/content-calendar');
                        else if (item.source_kind === 'client_meeting') navigate('/service-delivery');
                      }}
                      data-testid={`agenda-item-${item.type}-${item.id || index}`}
                    >
                      <div className={`w-1 h-10 rounded-full ${cfg.bar}`} />
                      <Icon className={`w-4 h-4 shrink-0 ${cfg.color}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {[cfg.label, item.subtitle].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      {item.type === 'reunion' && item.meet_link && (
                        <a
                          href={item.meet_link}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:text-blue-700"
                          title="Abrir videollamada"
                          data-testid={`agenda-meet-link-${item.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      {item.time ? (
                        <span className="text-xs font-medium text-muted-foreground shrink-0">{item.time}</span>
                      ) : null}
                      {item.type === 'personal' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteEvent(item.id); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-500 shrink-0"
                          title="Eliminar evento"
                          data-testid={`delete-personal-event-${item.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Middle Column - Resources */}
        <motion.div variants={itemVariants} className="space-y-6">
          {/* Progress Ring */}
          <Card className="card-hover gradient-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 ring-progress" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="10"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="10"
                      strokeLinecap="round"
                      strokeDasharray={`${(kpis?.week_completion_rate || 0) * 2.51} 251`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold">{kpis?.week_completion_rate || 0}%</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold">Progreso Semanal</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {kpis?.week_completed || 0} de {kpis?.week_total || 0} tareas completadas
                  </p>
                  {kpis?.week_change_pct != null && (
                    <div className={`flex items-center gap-1 mt-2 ${kpis.week_change_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`} data-testid="week-change">
                      <TrendingUp className={`w-4 h-4 ${kpis.week_change_pct < 0 ? 'rotate-180' : ''}`} />
                      <span className="text-sm">{kpis.week_change_pct >= 0 ? '+' : ''}{kpis.week_change_pct}% vs semana pasada</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right Column - Habits & Stats */}
        <motion.div variants={itemVariants} className="space-y-6">
          {/* Habits */}
          <Card className="card-hover">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Hábitos de Hoy
                </CardTitle>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setHabitDialog(true)} data-testid="add-habit-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="habits-today">
              {habits.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">No tienes hábitos configurados</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => setHabitDialog(true)} data-testid="create-habit-empty-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Crear hábito
                  </Button>
                </div>
              ) : (
                habits.map((habit) => {
                  const todayLog = habit.recent_logs?.find(
                    log => log.date === new Date().toISOString().split('T')[0]
                  );
                  return (
                    <div
                      key={habit.id}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                        todayLog?.completed ? 'bg-emerald-50' : 'bg-muted/30 hover:bg-muted/50'
                      }`}
                      data-testid={`habit-${habit.id}`}
                    >
                      <Checkbox
                        checked={todayLog?.completed || false}
                        onCheckedChange={(v) => handleHabitLog(habit.id, !!v)}
                        className="data-[state=checked]:bg-emerald-500"
                        data-testid={`habit-check-${habit.id}`}
                      />
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${todayLog?.completed ? 'text-emerald-700' : ''}`}>
                          {habit.name}
                        </p>
                      </div>
                      {habit.streak > 0 && (
                        <div className="flex items-center gap-1 text-amber-500">
                          <Flame className="w-4 h-4" />
                          <span className="text-xs font-medium">{habit.streak}</span>
                        </div>
                      )}
                      <button
                        onClick={() => handleDeleteHabit(habit.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-500"
                        title="Eliminar hábito"
                        data-testid={`delete-habit-${habit.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card className="card-hover">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Accesos Rápidos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => navigate('/journal')} className="h-auto py-3 flex flex-col items-center gap-1" data-testid="quick-journal">
                  <BookOpen className="w-5 h-5 text-primary" />
                  <span className="text-xs">Journal</span>
                </Button>
                <Button variant="outline" onClick={() => navigate('/content-calendar')} className="h-auto py-3 flex flex-col items-center gap-1" data-testid="quick-content-calendar">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="text-xs">Calendario de Contenido</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Diálogo crear hábito */}
      <Dialog open={habitDialog} onOpenChange={setHabitDialog}>
        <DialogContent className="max-w-sm" data-testid="habit-dialog">
          <DialogHeader><DialogTitle>Nuevo hábito</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>Nombre del hábito</Label>
            <Input
              value={habitName}
              onChange={(e) => setHabitName(e.target.value)}
              placeholder="Ej: Revisar métricas cada mañana"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateHabit(); }}
              data-testid="habit-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHabitDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateHabit} disabled={habitSaving} data-testid="save-habit-btn">
              {habitSaving ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo crear evento personal */}
      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
        <DialogContent className="max-w-sm" data-testid="personal-event-dialog">
          <DialogHeader><DialogTitle>Nuevo evento personal</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Añade un evento privado a tu agenda de hoy</DialogDescription>
          <div className="py-2 space-y-3">
            <div>
              <Label>Título</Label>
              <Input
                value={eventForm.titulo}
                onChange={(e) => setEventForm({ ...eventForm, titulo: e.target.value })}
                placeholder="Ej: Llamar al proveedor"
                data-testid="personal-event-title-input"
              />
            </div>
            <div>
              <Label>Hora (opcional)</Label>
              <Input
                type="time"
                value={eventForm.hora}
                onChange={(e) => setEventForm({ ...eventForm, hora: e.target.value })}
                data-testid="personal-event-time-input"
              />
            </div>
            <div>
              <Label>Nota (opcional)</Label>
              <Textarea
                value={eventForm.nota}
                onChange={(e) => setEventForm({ ...eventForm, nota: e.target.value })}
                placeholder="Detalles..."
                rows={2}
                data-testid="personal-event-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateEvent} disabled={eventSaving} data-testid="save-personal-event-btn">
              {eventSaving ? 'Guardando...' : 'Añadir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
